import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaPaperPlane, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { BOOKING_URL } from '../../config/links';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Captcha from '../Captcha/Captcha';
import BrandWave from '../BrandWave/BrandWave';
import './Contact.css';

const MIN_FORM_TIME_MS = 3000;

const Contact = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    website: '' // honeypot — humans never fill this
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const mountedAt = useRef(Date.now());
  const captchaRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot: silently drop — don't tell the bot it failed.
    if (formData.website) {
      showNotification(t('contact.form.success'), 'success');
      setFormData({ name: '', email: '', company: '', message: '', website: '' });
      return;
    }

    // Time-trap: reject submissions faster than a human can fill the form.
    if (Date.now() - mountedAt.current < MIN_FORM_TIME_MS) {
      showNotification(t('contact.form.botError'), 'error');
      return;
    }

    if (!formData.name || !formData.email || !formData.message) {
      showNotification(t('contact.form.error'), 'error');
      return;
    }

    if (!validateEmail(formData.email)) {
      showNotification(t('contact.form.emailError'), 'error');
      return;
    }

    if (!captchaRef.current || !captchaRef.current.verify()) {
      showNotification(t('contact.form.captchaError'), 'error');
      if (captchaRef.current) captchaRef.current.refresh();
      return;
    }

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'contactMessages'), {
        name: formData.name,
        email: formData.email,
        company: formData.company || '',
        message: formData.message,
        createdAt: serverTimestamp(),
        status: 'new',
        read: false
      });

      showNotification(t('contact.form.success'), 'success');
      setFormData({ name: '', email: '', company: '', message: '', website: '' });
      if (captchaRef.current) captchaRef.current.refresh();
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification(t('contact.form.sendError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact" ref={ref}>
      <div className="container">
        <motion.h1
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {t('contact.title')}
        </motion.h1>
        
        <div className="contact-content">
          <motion.div 
            className="contact-info"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3>{t('contact.subtitle')}</h3>
            <p>{t('contact.description')}</p>
            
            <div className="contact-items">
              <div className="contact-item">
                <FaPhone />
                <span>{t('contact.phone')}</span>
              </div>
              <div className="contact-item">
                <FaEnvelope />
                <span>{t('contact.email')}</span>
              </div>
            </div>

            {/* Brand visual (particle wave + slogan) doubling as the demo booking card */}
            <div className="booking-card">
              <BrandWave />
              <div className="booking-card-overlay" aria-hidden="true" />
              <div className="booking-card-content">
                <p className="booking-slogan" lang="en">
                  <span>{t('hero.sloganLine1')}</span>
                  <span className="accent">{t('hero.sloganLine2')}</span>
                </p>
                <h4>{t('booking.title')}</h4>
                <p>{t('booking.text')}</p>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  <FaCalendarAlt /> {t('booking.button')}
                </a>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            className="contact-form-container"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3>{t('contact.form.title')}</h3>
            <p className="form-subtitle">{t('contact.form.subtitle')}</p>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">
                  {t('contact.form.name')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.form.namePlaceholder')}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  {t('contact.form.email')} <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact.form.emailPlaceholder')}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">{t('contact.form.company')}</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder={t('contact.form.companyPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  {t('contact.form.message')} <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.messagePlaceholder')}
                  rows="5"
                  required
                ></textarea>
              </div>

              {/* Honeypot — hidden from users via CSS and from screen readers via aria-hidden */}
              <div className="hp-field" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <Captcha
                ref={captchaRef}
                label={t('contact.form.captchaLabel')}
                placeholder={t('contact.form.captchaPlaceholder')}
              />

              <motion.button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaPaperPlane />
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
              </motion.button>

              <p className="form-privacy-note">
                {t('contact.form.privacyNote')}{' '}
                <Link to="/privacy-policy">{t('contact.form.privacyLink')}</Link>
              </p>
            </form>
          </motion.div>
        </div>
        
        {notification && (
          <motion.div 
            className={`notification ${notification.type}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {notification.message}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Contact;